import { PostSort, type PostListQuery, type PostStatusDto } from '@gr/core/content/posts/dto';
import { META_STATUSES, isMetaStatus, type MetaStatus } from '@gr/core/content/posts/meta-status';

/** Фильтры списка постов (POST-UC-6) — состояние страницы, не форма. */
export interface PostFilters {
  q: string;
  tag: string;
  statusId: string;
  meta: '' | MetaStatus;
  /** POST-UC-6: явно выбранное правило оформления; '' — любое. */
  formatId: string;
  sort: PostSort;
}

export const EMPTY_POST_FILTERS: PostFilters = { q: '', tag: '', statusId: '', meta: '', formatId: '', sort: PostSort.Updated };

export function isFiltered(filters: PostFilters, linkedTo: string | undefined): boolean {
  return Boolean(filters.q || filters.tag || filters.statusId || filters.meta || filters.formatId || linkedTo);
}

export function listQueryOf(filters: PostFilters, linkedTo: string | undefined): PostListQuery {
  return {
    q: filters.q,
    tag: filters.tag,
    statusId: filters.statusId || undefined,
    meta: filters.meta || undefined,
    formatId: filters.formatId || undefined,
    sort: filters.sort,
    linkedTo,
  };
}

/**
 * POST-UC-6: статус и состояние — один фильтр. Значение — `status:<id>`, `meta:<состояние>`
 * или '' (любой статус).
 */
export function statusFilterValue(filters: PostFilters): string {
  if (filters.statusId) return statusOption(filters.statusId);
  if (filters.meta) return metaOption(filters.meta);
  return '';
}

export function withStatusFilter(filters: PostFilters, value: string): PostFilters {
  if (value.startsWith(STATUS_PREFIX)) return { ...filters, statusId: value.slice(STATUS_PREFIX.length), meta: '' };
  const meta = value.slice(META_PREFIX.length);
  if (value.startsWith(META_PREFIX) && isMetaStatus(meta)) return { ...filters, statusId: '', meta };
  return { ...filters, statusId: '', meta: '' };
}

export interface StatusFilterGroup {
  meta: MetaStatus;
  /** Вариант «все статусы состояния» — только когда статусов в состоянии больше одного. */
  metaOption: string | undefined;
  statuses: readonly { option: string; name: string }[];
}

/** Статусы блога по состояниям в системном порядке; состояния без статусов пропускаются. */
export function statusFilterGroups(statuses: readonly PostStatusDto[]): StatusFilterGroup[] {
  return META_STATUSES.flatMap((meta) => {
    const own = statuses.filter((s) => s.meta === meta).sort((a, b) => a.position - b.position);
    if (own.length === 0) return [];
    return [{ meta, metaOption: own.length > 1 ? metaOption(meta) : undefined, statuses: own.map((s) => ({ option: statusOption(s.id), name: s.name })) }];
  });
}

const STATUS_PREFIX = 'status:';
const META_PREFIX = 'meta:';
const statusOption = (statusId: string): string => `${STATUS_PREFIX}${statusId}`;
const metaOption = (meta: MetaStatus): string => `${META_PREFIX}${meta}`;
